import type { ApiClientV2 } from 'utils/api-client/api-client.utils'

import { FromSwagger, FromSwaggerWithoutId } from 'types/helpers'
import {
  ShopEventAttendeeAttributes,
  ShopEventAttendeeDetailAttributes,
  ShopEventAttendeeErrorsResponse,
} from 'types/__generated__/api'

export type EventAttendeeSeatV2 = FromSwagger<ShopEventAttendeeAttributes>
export type EventAttendeeDetailV2 = FromSwagger<ShopEventAttendeeDetailAttributes>
export type EventAttendeeDetailUpdate = FromSwaggerWithoutId<ShopEventAttendeeDetailAttributes>

export type EventAttendeeError = ShopEventAttendeeErrorsResponse['errors'][number]

export type EventAttendeeListResponse =
  | { data: Record<string, any>[]; included?: Record<string, any>[]; totalCount?: number; status?: number }
  | { errors: EventAttendeeError[]; status?: number }

export type EventAttendeeBulkUpdateResponse =
  | { data: Record<string, any>[]; included?: Record<string, any>[]; status?: number }
  | { errors: EventAttendeeError[]; status?: number }

export type TicketTypeListResponse =
  | { data: Record<string, any>[]; totalCount?: number; status?: number }
  | { errors: EventAttendeeError[]; status?: number }

export interface TicketTypeOptionV2 {
  value: number
  label: string
}

export const eventAttendeesFailed = (
  resp: EventAttendeeListResponse | EventAttendeeBulkUpdateResponse | TicketTypeListResponse | undefined
): resp is { errors: EventAttendeeError[]; status?: number } =>
  Array.isArray((resp as { errors?: unknown } | undefined)?.errors) &&
  (resp as { errors: EventAttendeeError[] }).errors.length > 0

// 404 = unknown order token, an attendee id outside this order, or the feature gate is off.
export const eventAttendeesClosed = (
  resp: EventAttendeeListResponse | EventAttendeeBulkUpdateResponse | undefined
): boolean => {
  if (!eventAttendeesFailed(resp)) return false
  return Number(resp.status ?? resp.errors[0]?.status) === 404
}

export interface EventAttendeeBulkUpdateItem {
  id: string
  type: 'event_attendee_details'
  attributes: EventAttendeeDetailUpdate
}

export interface EventAttendeeBulkUpdateRequest {
  data: EventAttendeeBulkUpdateItem[]
  meta: {
    // Only checked when the seller-side Statsig flag is on; bypassed on staging.
    recaptcha?: string
  }
}

export interface EventAttendeeListFilters {
  personalisation_status?: 'unassigned' | 'pending' | 'queued' | 'sent' | 'personalised' | 'transferred'
  ticket_id?: number
  query?: string
}

export interface EventAttendeeListParams {
  filter?: EventAttendeeListFilters
  sort?: 'id' | '-id' | 'ticket_name' | '-ticket_name'
  page?: { number?: number; size?: number }
}

const BASE_URL = (shop: string, token: string) => `shop/${shop}/orders/${token}/event_attendees`

// One page big enough to hold every ticket type an order could realistically carry, capped at the
// backend's 1000-record ceiling — so the filter's options are complete in a single request.
const TICKET_TYPES_PAGE_SIZE = 1000

// Client is read at call time: a module-level store has no root, so destructuring GET/PUT at
// import throws (same defensive pattern as attendeeAssignments.api.ts).
export const createEventAttendeesV2Api = (client?: ApiClientV2) => ({
  // Every seat of the order with its attendee row sideloaded (`included[]`, type
  // `event_attendee_details`). Server-side filter/sort/page — the store never filters client-side.
  fetchList: (shop: string, token: string, params?: EventAttendeeListParams): Promise<EventAttendeeListResponse> => {
    if (!client) {
      return Promise.resolve({ errors: [{ code: 'no_client', status: '0', title: 'No API client.' }] })
    }

    return client.GET<Record<string, any>[]>(
      BASE_URL(shop, token),
      params
    ) as unknown as Promise<EventAttendeeListResponse>
  },

  // One transaction: a refusal on any row rolls the whole batch back and writes none. Response
  // returns the written seats in the list shape, so callers re-render from it, no refetch needed.
  bulkUpdate: (
    shop: string,
    token: string,
    data: EventAttendeeBulkUpdateRequest
  ): Promise<EventAttendeeBulkUpdateResponse> => {
    if (!client) {
      return Promise.resolve({ errors: [{ code: 'no_client', status: '0', title: 'No API client.' }] })
    }

    return client.PUT<Record<string, any>[]>(
      `${BASE_URL(shop, token)}/bulk_update`,
      data
    ) as unknown as Promise<EventAttendeeBulkUpdateResponse>
  },

  // The endpoint is paginated (meta.stats.total.count), but an order's ticket types are a bounded
  // set (well under the BE 1000 cap), so one large page returns them all — no async/on-demand
  // loading needed for a filter dropdown this small.
  fetchTicketTypes: (shop: string, token: string): Promise<TicketTypeListResponse> => {
    if (!client) {
      return Promise.resolve({ errors: [{ code: 'no_client', status: '0', title: 'No API client.' }] })
    }

    return client.GET<Record<string, any>[]>(`shop/${shop}/orders/${token}/ticket_types`, {
      page: { size: TICKET_TYPES_PAGE_SIZE },
    }) as unknown as Promise<TicketTypeListResponse>
  },
})

export type EventAttendeesV2Api = ReturnType<typeof createEventAttendeesV2Api>
